import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import { AlertCircle, Loader2, ChevronLeft, ChevronRight, Check, Pencil } from 'lucide-react';
import api from '../api/api';
import { formatChoice } from '../utils/csiReport';
import CSIAssessmentSkeleton from './layout/CSIAssessmentSkeleton';

// 1-5 Likert agreement scale, plus N/A (not scored). Colors run red -> green
// across the agreement slider (see AgreementSlider).
const SCALE = [
    { value: 1, label: 'Strongly Disagree', color: '#ef4444' },
    { value: 2, label: 'Disagree', color: '#f97316' },
    { value: 3, label: 'Neutral', color: '#eab308' },
    { value: 4, label: 'Agree', color: '#84cc16' },
    { value: 5, label: 'Strongly Agree', color: '#22c55e' },
];
const RAIL_GRADIENT = 'linear-gradient(90deg,#ef4444 0%,#f97316 25%,#eab308 50%,#84cc16 75%,#22c55e 100%)';
const NA = 'na';

// Category dot color + tagline (Alicia's copy) shown at the top of each
// category page. Matched by substring so it survives naming variants
// (e.g. Community may be stored as "Giving Back").
const categoryMeta = (name) => {
    const k = String(name || '').toLowerCase();
    if (k.includes('performance')) return { color: '#6366f1', desc: 'how you show up' };
    if (k.includes('skilling')) return { color: '#0ea5e9', desc: 'how you grow' };
    if (k.includes('network')) return { color: '#14b8a6', desc: 'who you connect with' };
    if (k.includes('community') || k.includes('giving')) return { color: '#f59e0b', desc: 'how you contribute' };
    if (k.includes('feedback')) return { color: '#ec4899', desc: 'how you learn' };
    return { color: '#94a3b8', desc: '' };
};

/**
 * AgreementSlider: a 5-stop gradient slider for one Likert question, with an N/A
 * toggle on the right. Starts with no thumb (no default value biases answers);
 * a thumb appears once the user picks a stop. Click a stop, click anywhere on
 * the rail (snaps to nearest), or use arrow keys. N/A greys the rail out.
 *
 * value: 1..5 | 'na' | undefined. onChange(next) with the same shape.
 */
function AgreementSlider({ value, onChange, isDarkMode, label }) {
    const railRef = useRef(null);
    const skipped = value === NA;
    const selected = typeof value === 'number' ? value : null;
    const ring = isDarkMode ? '#1e293b' : '#ffffff';

    const pickNearest = (clientX) => {
        const row = railRef.current;
        if (!row) return;
        const r = row.getBoundingClientRect();
        const idx = Math.round(((clientX - r.left - 20) / (r.width - 40)) * 4);
        onChange(Math.min(5, Math.max(1, idx + 1)));
    };

    const onKeyDown = (e) => {
        const k = e.key;
        if (!['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown', 'Home', 'End'].includes(k)) return;
        e.preventDefault();
        if (k === 'Home') return onChange(1);
        if (k === 'End') return onChange(5);
        if (selected == null) return onChange(3);
        if (k === 'ArrowRight' || k === 'ArrowUp') return onChange(Math.min(5, selected + 1));
        return onChange(Math.max(1, selected - 1));
    };

    return (
        <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
                <div
                    ref={railRef}
                    role="slider"
                    tabIndex={0}
                    aria-label={label}
                    aria-valuemin={1}
                    aria-valuemax={5}
                    aria-valuenow={selected || undefined}
                    aria-valuetext={selected ? SCALE[selected - 1].label : (skipped ? 'Not applicable' : 'No answer selected')}
                    onKeyDown={onKeyDown}
                    onClick={(e) => { if (!e.target.closest('button')) pickNearest(e.clientX); }}
                    className="relative h-8 cursor-pointer outline-none rounded-lg focus-visible:ring-2 focus-visible:ring-primary-500/60"
                >
                    {/* gradient rail (inset so its ends sit under the first/last stop) */}
                    <div
                        className={`absolute left-5 right-5 top-1/2 -translate-y-1/2 h-1.5 rounded-full transition ${skipped ? 'grayscale opacity-40' : ''}`}
                        style={{ background: RAIL_GRADIENT }}
                    />
                    {/* 5 stops */}
                    <div className="absolute inset-0 flex items-center justify-between">
                        {SCALE.map(t => {
                            const isSel = selected === t.value && !skipped;
                            const dot = skipped ? '#cbd5e1' : t.color;
                            return (
                                <button
                                    key={t.value}
                                    type="button"
                                    aria-label={t.label}
                                    onClick={() => onChange(t.value)}
                                    className="w-10 h-8 flex items-center justify-center group"
                                >
                                    <span
                                        className="rounded-full transition-transform group-hover:scale-125"
                                        style={isSel
                                            ? { width: 20, height: 20, background: dot, boxShadow: `0 0 0 3px ${ring}, 0 2px 6px rgba(15,23,42,.28)` }
                                            : { width: 4, height: 15, background: dot, boxShadow: `0 0 0 3px ${ring}` }}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
                {/* anchor labels */}
                <div className="flex justify-between mt-1.5 px-1 text-[10px] sm:text-[11px] font-semibold">
                    <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>Strongly disagree</span>
                    <span className={isDarkMode ? 'text-amber-400' : 'text-amber-600'}>Neutral</span>
                    <span className={isDarkMode ? 'text-primary-400' : 'text-primary-600'}>Strongly agree</span>
                </div>
            </div>

            <button
                type="button"
                onClick={() => onChange(skipped ? undefined : NA)}
                aria-pressed={skipped}
                className={`shrink-0 h-9 px-3.5 rounded-xl border text-xs font-bold transition-colors ${skipped
                    ? 'bg-slate-500 border-slate-500 text-white shadow-sm'
                    : isDarkMode ? 'bg-slate-700/60 border-slate-600 text-slate-400 hover:border-slate-400' : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'}`}
            >
                N/A
            </button>
        </div>
    );
}

/**
 * StepProgress: node-based stepper across the assessment's categories. Desktop
 * (md+) shows the category name under each node (variant B); mobile shows the
 * current category name + "Step X of N" above the nodes only (variant A). A node
 * is a check once its category is fully answered, the current step is a larger
 * haloed node, upcoming steps are small muted dots. Clicking a node navigates,
 * gated the same way as Next: you can only jump to a step whose earlier steps
 * are all complete (so backward is always allowed, forward only through done
 * steps).
 */
function StepProgress({ areas, step, answers, onGoto, isDarkMode }) {
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const N = areas.length;
    const total = N + 1;   // the N category screens plus a final Review step
    const REVIEW = N;      // index of the Review node
    const areaLikert = (a) => (a?.items || []).filter(q => (q.QuestionType || 'likert') === 'likert');
    const answeredAll = (a) => areaLikert(a).every(q => answers[q.QuestionID] != null);
    const canGoTo = (i) => (i === REVIEW ? areas.every(answeredAll) : areas.slice(0, i).every(answeredAll));
    const statusOf = (i) => (i === step ? 'current' : (i < N && answeredAll(areas[i]) ? 'done' : 'todo'));
    const labelOf = (i) => (i === REVIEW ? 'Review' : areas[i].focusAreaName);

    // Nodes are centered in `total` equal columns, so their centers sit at
    // (i+0.5)/total. The connecting line spans from the first center to the last
    // (inset from each edge), and the emerald fill reaches the current node's
    // center. This keeps every label perfectly under its node regardless of size.
    const inset = total > 1 ? 50 / total : 50; // % from each edge to the first/last node center
    const fillWidth = total > 1 ? (100 - 2 * inset) * (step / (total - 1)) : 0;

    return (
        <div className={`rounded-2xl border shadow-sm p-4 md:p-5 mb-5 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            {/* Mobile (variant A): current category name above the nodes (the
                nodes themselves already show which step / how many, so no
                "Step X of N"). Desktop shows the name under each node instead. */}
            <div className={`md:hidden text-sm font-semibold mb-3 ${heading}`}>
                {step >= N ? 'Review' : areas[step]?.focusAreaName}
            </div>

            <div className="relative">
                {/* connecting line (sits behind the nodes, at their vertical center) */}
                <div
                    className={`absolute top-5 -translate-y-1/2 h-[3px] rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
                    style={{ left: `${inset}%`, right: `${inset}%` }}
                />
                <div
                    className="absolute top-5 -translate-y-1/2 h-[3px] rounded-full bg-primary-500 transition-all"
                    style={{ left: `${inset}%`, width: `${fillWidth}%` }}
                />

                {/* node + label columns (equal width -> nodes evenly spaced) */}
                <div className="relative grid" style={{ gridTemplateColumns: `repeat(${total}, minmax(0,1fr))` }}>
                    {Array.from({ length: total }).map((_, i) => {
                        const st = statusOf(i);
                        const reachable = canGoTo(i);
                        const label = labelOf(i);
                        const tint = st === 'current' ? 'text-primary-600' : st === 'done' ? sub : (isDarkMode ? 'text-slate-500' : 'text-slate-400');
                        return (
                            <div key={i} className="flex flex-col items-center min-w-0">
                                <div className="h-10 flex items-center justify-center">
                                    <button
                                        type="button"
                                        disabled={!reachable}
                                        onClick={() => reachable && onGoto(i)}
                                        aria-current={st === 'current' ? 'step' : undefined}
                                        aria-label={`${label}${st === 'done' ? ' (completed)' : st === 'current' ? ' (current step)' : ''}`}
                                        className={`shrink-0 flex items-center justify-center rounded-full font-bold transition-all ${reachable ? 'cursor-pointer' : 'cursor-default'} ${st === 'current'
                                            ? 'w-9 h-9 text-sm bg-primary-600 text-white shadow-[0_0_0_5px_rgba(16,185,129,0.16)]'
                                            : st === 'done'
                                                ? 'w-7 h-7 text-sm bg-primary-500 text-white'
                                                : `w-6 h-6 text-xs ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-400'}`}`}
                                    >
                                        {st === 'done' ? <Check size={15} strokeWidth={3} /> : i + 1}
                                    </button>
                                </div>
                                {/* label: desktop only (variant B), centered under its node */}
                                <div className={`hidden md:block w-full truncate px-1 mt-1 text-center text-[11.5px] font-semibold ${tint}`}>
                                    {label}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/**
 * CSIAssessmentForm: the Career Success Index questionnaire, one category per
 * screen (wizard). Renders inner content only (no page wrapper).
 *
 * Props:
 *   onComplete(result)  called after a successful submit
 *   submitLabel         text for the final submit button
 */
export default function CSIAssessmentForm({ onComplete, submitLabel = 'See my results' }) {
    const { isDarkMode } = useTheme();

    const [areas, setAreas] = useState([]);
    const [answers, setAnswers] = useState({}); // qid -> 1..5 | 'na' | choice string
    const [step, setStep] = useState(0);
    const [reviewEditing, setReviewEditing] = useState(false); // true while editing a category reached from the Review step
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get('/csi/questions');
                if (alive) setAreas(res.data?.areas || []);
            } catch {
                if (alive) setError('Could not load the assessment. Please try again.');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // After moving between category screens, scroll back to the top so each
    // section starts from the top instead of wherever the user was scrolled.
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

    const setAnswer = (qid, val) => setAnswers(prev => ({ ...prev, [qid]: val }));

    const cardCls = `rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';

    const body = isDarkMode ? 'text-slate-300' : 'text-slate-700';
    const N = areas.length;
    const isReview = N > 0 && step === N; // the final Review step sits at index N
    const current = areas[step];
    const likert = useMemo(() => (current?.items || []).filter(q => (q.QuestionType || 'likert') === 'likert'), [current]);
    const validation = useMemo(() => (current?.items || []).filter(q => q.QuestionType === 'validation'), [current]);

    // A category screen is complete once every likert question has a selection
    // (a rating or N/A). Validation questions are optional.
    const stepComplete = likert.every(q => answers[q.QuestionID] != null);
    // The whole assessment is submittable once every category is complete.
    const allComplete = useMemo(() => areas.every(a =>
        (a.items || []).filter(q => (q.QuestionType || 'likert') === 'likert').every(q => answers[q.QuestionID] != null)
    ), [areas, answers]);

    // On the last category, Next advances to the Review step (index N) rather
    // than submitting; the submit button lives on the Review step.
    const goNext = () => { if (stepComplete) setStep(s => (s === N - 1 ? N : s + 1)); };
    const goBack = () => {
        if (isReview) { setReviewEditing(true); setStep(N - 1); } // Review -> back into the last category
        else setStep(s => Math.max(0, s - 1));
    };
    // Jumping from the Review step into a category (via a stepper node) is an edit.
    const goToStep = (i) => { if (i < N && step === N) setReviewEditing(true); setStep(i); };
    // "Edit" on the Review summary opens that category in review-edit mode.
    const startEdit = (i) => { setReviewEditing(true); setStep(i); };

    // Renders the stored answer for the Review summary: a colored Likert chip,
    // an N/A / not-answered chip, or a validation choice chip.
    const answerChip = (val) => {
        const base = `inline-flex items-center gap-1.5 shrink-0 text-[11.5px] font-bold rounded-full border px-2.5 py-1 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`;
        if (val == null) return <span className={`${base} ${sub}`}>Not answered</span>;
        if (val === NA) return <span className={`${base} ${sub}`}><span className="w-2 h-2 rounded-full bg-slate-400" /> N/A</span>;
        if (typeof val === 'number') {
            const s = SCALE[val - 1];
            return <span className={`${base} ${heading}`}><span className="w-2 h-2 rounded-full" style={{ background: s.color }} /> {s.label}</span>;
        }
        return <span className={`${base} ${sub}`}>{formatChoice(val)}</span>; // validation choice
    };

    const submit = async () => {
        if (!allComplete || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const payload = {};
            Object.entries(answers).forEach(([qid, val]) => {
                payload[qid] = val === NA ? null : val; // N/A -> null (not scored)
            });
            const res = await api.post('/csi/submit', { answers: payload });
            onComplete?.(res.data);
        } catch (err) {
            // Surface the backend message (e.g. the monthly retake limit) when present.
            setError(err.response?.data?.error || 'Submission failed. Please try again.');
            setSubmitting(false);
        }
    };

    if (loading) return <CSIAssessmentSkeleton isDarkMode={isDarkMode} />;
    if (!areas.length) {
        return <div className={`${cardCls} p-8 text-center ${sub}`}>This assessment isn’t available yet. Please check back soon.</div>;
    }

    const btnBase = 'rounded-lg text-sm font-semibold border transition-colors py-2 px-2 text-center leading-tight';

    return (
        <div>
            {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm mb-4">
                    <AlertCircle size={16} /> {error}
                </div>
            )}

            {/* Step progress (node stepper, with a final Review node) */}
            <StepProgress
                areas={areas}
                step={step}
                answers={answers}
                onGoto={goToStep}
                isDarkMode={isDarkMode}
            />

            {isReview ? (
                /* Review summary: every category's questions + the stored answer,
                   with an Edit shortcut per section, before the real submit. */
                <div>
                    <h2 className={`text-xl md:text-2xl font-extrabold ${heading}`}>Review your answers</h2>
                    <p className={`text-sm mt-1 mb-5 ${sub}`}>Check everything looks right. You can edit any section, then submit to see your results.</p>
                    {areas.map((a, ai) => (
                        <div key={a.focusAreaId ?? ai} className={`${cardCls} p-4 md:p-5 mb-4`}>
                            <div className="flex items-center justify-between mb-1">
                                <h3 className={`font-bold ${heading}`}>{a.focusAreaName}</h3>
                                <button
                                    type="button"
                                    onClick={() => startEdit(ai)}
                                    className={`inline-flex items-center gap-1.5 text-sm font-semibold ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
                                >
                                    <Pencil size={14} /> Edit
                                </button>
                            </div>
                            <div className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                                {(a.items || []).map((q, qi) => (
                                    <div key={q.QuestionID} className="flex items-center justify-between gap-4 py-2.5">
                                        <p className={`text-[13px] leading-snug ${body}`}>
                                            <span className={sub}>{qi + 1}. </span>{q.QuestionText}
                                        </p>
                                        {answerChip(answers[q.QuestionID])}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    {/* Current category (dot + name + tagline), one row */}
                    {current && (
                        <div className={`flex items-center gap-3 mb-6 rounded-xl border px-4 py-3 text-sm ${isDarkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white'}`}>
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: categoryMeta(current.focusAreaName).color }} />
                            <span className={`font-semibold ${heading}`}>{current.focusAreaName}</span>
                            {categoryMeta(current.focusAreaName).desc && (
                                <span className={sub}>— {categoryMeta(current.focusAreaName).desc}</span>
                            )}
                        </div>
                    )}

                    {/* Likert questions for this category */}
                    <div className="space-y-6">
                        {likert.map((q, idx) => (
                            <div key={q.QuestionID}>
                                <p className={`text-sm mb-3 ${heading}`}>
                                    <span className={sub}>{idx + 1}. </span>{q.QuestionText}
                                </p>
                                <AgreementSlider
                                    value={answers[q.QuestionID]}
                                    onChange={(v) => setAnswer(q.QuestionID, v)}
                                    isDarkMode={isDarkMode}
                                    label={q.QuestionText}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Validation question (optional, choice) */}
                    {validation.map(q => (
                        <div key={q.QuestionID} className={`${cardCls} p-4 mt-6`}>
                            <p className={`text-sm mb-3 ${heading}`}>{q.QuestionText}</p>
                            <div className="flex flex-wrap gap-1.5">
                                {(q.ChoiceOptions || []).map(opt => {
                                    const selected = answers[q.QuestionID] === opt;
                                    return (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => setAnswer(q.QuestionID, selected ? undefined : opt)}
                                            className={`${btnBase} flex-1 min-w-[72px] px-2 ${selected
                                                ? 'bg-primary-500 border-primary-500 text-white'
                                                : isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:border-primary-500' : 'bg-white border-slate-300 text-slate-600 hover:border-primary-500'}`}
                                        >
                                            {formatChoice(opt)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* Nav */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-8">
                <button
                    onClick={goBack}
                    disabled={!isReview && step === 0}
                    className={`flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-semibold border disabled:opacity-40 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                >
                    <ChevronLeft size={16} /> Back
                </button>

                <div className="flex flex-wrap items-center gap-3 ml-auto">
                {!isReview && !stepComplete && (() => {
                    const n = likert.filter(q => answers[q.QuestionID] == null).length;
                    return (
                        <span className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                            {n} response{n === 1 ? '' : 's'} left
                        </span>
                    );
                })()}

                {isReview ? (
                    <button
                        onClick={submit}
                        disabled={!allComplete || submitting}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting && <Loader2 className="animate-spin" size={16} />}
                        {submitting ? 'Scoring…' : submitLabel}
                    </button>
                ) : reviewEditing ? (
                    <button
                        onClick={() => setStep(N)}
                        className="flex items-center gap-1 px-6 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors"
                    >
                        <ChevronLeft size={16} /> Back to review
                    </button>
                ) : (
                    <button
                        onClick={goNext}
                        disabled={!stepComplete}
                        className="flex items-center gap-1 px-6 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next <ChevronRight size={16} />
                    </button>
                )}
                </div>
            </div>
        </div>
    );
}
